<script lang="ts">
  /* THE BOTTOM OF A PHONE, AS ONE OBJECT.
   *
   * ═══ WHAT THIS REPLACES ═══
   *
   * Five things used to anchor themselves independently to the bottom edge —
   * the scale bar, the time control, the tool row, the LAYERS button and the
   * credit line — in four components, each holding its own arithmetic about
   * where the others were:
   *
   *     .maplibregl-ctrl-bottom-left { bottom: calc(var(--gd-controls-bottom) + 100px) !important }
   *     .scrubwrap                   { bottom: calc(var(--gd-controls-bottom) + 52px) }
   *     .tools                       { left: calc(104px + env(safe-area-inset-left)) }
   *     footer                       { bottom: env(safe-area-inset-bottom) }
   *
   * Every one of those numbers is an unenforced claim about some other
   * element's rendered size, and the whole history of this layout is one of
   * them landing on another. Measured on an iPhone 14 Pro they occupied a
   * 162 px band in four stripes with gaps between them — a quarter of the
   * screen, most of it air.
   *
   * ═══ AND IT RENDERS FROM THE SAME LIST THE RAIL DOES ═══
   *
   * That is the change from the shipped version, which hardcoded three slots.
   * A panel registered once appears in both, so the phone and the desktop
   * cannot disagree about what this app can do.
   */
  import type { Snippet } from 'svelte';
  import Icon from '../primitives/Icon.svelte';
  import { panels } from './panels.svelte';

  let {
    /** The chart's own controls — zoom, and whatever else acts on the map
     *  rather than opening a panel. Kept as a snippet because they are the
     *  host's, and the shell has no business knowing what a MapLibre instance
     *  is. */
    tools,
    /** The credit line. NOT decoration: the OGL-Canada acknowledgement is the
     *  condition the public ENC proxy is licensed under, which is why the sheet
     *  stops on top of this element rather than covering it. */
    fineprint
  }: { tools?: Snippet; fineprint?: Snippet } = $props();

  /* ═══ HOW MANY SLOTS FIT, AND WHAT HAPPENS TO THE REST ═══
   *
   * Five across a 393 px screen is ~72 px each. That is under the 18–21 mm
   * (114–133 px) the touchscreen-under-vibration work says a critical control
   * wants on a moving deck, and it is well over Apple's 44 px floor with the
   * height untouched at 46. It is a trade, said out loud: the alternative is
   * three slots at 125 px and a navigation that cannot reach half the app.
   *
   * Beyond five, the tail folds into MORE rather than shrinking — a row that
   * overflows is how the time control ended up 177 px off the right edge of
   * every phone, in production, with the whole proof suite green. */
  const MAX_SLOTS = 5;

  const items = $derived(panels.nav());

  /* Pinned entries keep their slot whatever else is registered — see `pin` in
   * panels.svelte.ts. Everything else fills the room left over, in order, and
   * the tail folds into MORE.
   *
   * Partitioned rather than sliced, then filtered back through the original
   * list, so the visible row stays in registration order: pinning the time
   * entry must not move it to the front, because the running order is an
   * editorial judgement and this file does not get to make it. */
  const shown = $derived.by(() => {
    if (items.length <= MAX_SLOTS) return items;
    const isPinned = (p: (typeof items)[number]) =>
      typeof p.pin === 'function' ? p.pin() : p.pin === true;
    const pinned = items.filter(isPinned);
    const rest = items.filter((p) => !isPinned(p));
    const room = Math.max(0, MAX_SLOTS - 1 - pinned.length);
    const keep = new Set(rest.slice(0, room));
    return items.filter((p) => isPinned(p) || keep.has(p));
  });
  const overflow = $derived(items.filter((p) => !shown.includes(p)));
  const overflowOpen = $derived(overflow.some((p) => panels.is(p.id)));

  let showMore = $state(false);


  /* ═══ HOW MUCH ROOM THE DOCK IS TAKING ═══
   *
   * Measured, not declared, because the height depends on the safe-area inset
   * and on how tall the host draws its fine print — neither of which this file
   * gets to decide. Published on the root because the consumers are CSS in
   * other components, and there is nothing to re-render on either side.
   *
   * This looks like the `--gd-lift` it replaced and is its opposite: lift was
   * the SHEET publishing a height that changed sixty times a second during a
   * drag, read by six selectors across three files, so five controls could
   * scramble out of its way. This is one fixed object publishing one number
   * that changes when the phone rotates. */
  let dockEl = $state<HTMLElement | null>(null);
  $effect(() => {
    const el = dockEl;
    if (!el) return;
    const root = document.documentElement;
    const measure = () => root.style.setProperty('--gd-dock-h', `${el.offsetHeight}px`);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--gd-dock-h');
    };
  });
</script>

<div class="dock" bind:this={dockEl}>
  {#if showMore && overflow.length}
    <!-- Above the row, not a menu floating off it: the sheet already owns the
         space over the dock, and a popover that can be covered by the thing it
         opens is the trap this layout exists to avoid. -->
    <div class="more" role="group" aria-label="more panels">
      {#each overflow as p (p.id)}
        {@const off = p.available?.() === false}
        <button
          class="slot"
          class:on={panels.is(p.id)}
          class:off
          title={off ? `${p.title} — ${p.reason?.() ?? p.title}` : p.title}
          aria-label={off ? `${p.title} — ${p.reason?.() ?? p.title}` : p.title}
          aria-disabled={off ? 'true' : undefined}
          onclick={() => {
            panels.toggle(p.id);
            showMore = false;
          }}
        >
          <span class="ic">
        <!-- A live reading REPLACES the mark — see `readout` in the registry.
             The time entry shows "06:40" and the tools entry shows the armed
             tool's label, because in both cases the state is what you needed to
             see without opening anything. -->
        {#if p.readout?.()}
          <span class="readout">{p.readout()}</span>
        {:else}
          <Icon name={p.icon} />
        {/if}
      </span>
          <span class="lb">{p.title}</span>
        </button>
      {/each}
    </div>
  {/if}

  <div class="slots">
    {#each shown as p (p.id)}
      {@const off = p.available?.() === false}
      <button
        class="slot"
        class:on={panels.is(p.id)}
        class:warn={p.tone?.() === 'warn'}
        class:off
        title={off ? `${p.title} — ${p.reason?.() ?? p.title}` : p.title}
        aria-label={off ? `${p.title} — ${p.reason?.() ?? p.title}` : p.title}
        aria-disabled={off ? 'true' : undefined}
        aria-pressed={panels.is(p.id)}
        onclick={() => {
          panels.toggle(p.id);
          showMore = false;
        }}
      >
        <span class="ic">
        <!-- A live reading REPLACES the mark — see `readout` in the registry.
             The time entry shows "06:40" and the tools entry shows the armed
             tool's label, because in both cases the state is what you needed to
             see without opening anything. -->
        {#if p.readout?.()}
          <span class="readout">{p.readout()}</span>
        {:else}
          <Icon name={p.icon} />
        {/if}
      </span>
        <span class="lb">{p.title}</span>
      </button>
    {/each}

    {#if overflow.length}
      <button
        class="slot"
        class:on={showMore || overflowOpen}
        aria-expanded={showMore}
        aria-label="more panels"
        onclick={() => (showMore = !showMore)}
      >
        <span class="ic"><Icon name="more" /></span>
        <span class="lb">More</span>
      </button>
    {/if}

    {#if tools}{@render tools()}{/if}
  </div>

  {#if fineprint}
    <div class="fineprint">{@render fineprint()}</div>
  {/if}
</div>

<style>
  .dock {
    /* Above the sheet. The dock is the one thing that is always reachable — a
       panel that could cover the way out of itself is a trap. */
    z-index: 8;
    display: flex;
    flex-direction: column;
    gap: var(--sp-2);
    background: var(--s-1);
    border-top: 1px solid var(--line);
    /* iOS Safari: without this, the browser reserves gesture/double-tap
       interpretation before synthesizing click, making taps feel dead.
       Applied to .dock (not just .slot) so host-injected tools get it too. */
    touch-action: manipulation;
    /* ═══ max(), NOT THE BARE env() ═══
       WebKit toggles `safe-area-inset-bottom` to ZERO when Safari's toolbar
       collapses, which is exactly when the page is tallest and the dock is
       closest to the home indicator. The bare value put the credit line under
       it at the precise moment there was most room. */
    padding: var(--sp-2) max(env(safe-area-inset-right), var(--sp-5))
      max(env(safe-area-inset-bottom), var(--sp-6)) max(env(safe-area-inset-left), var(--sp-5));
  }

  .slots {
    display: flex;
    gap: var(--sp-3);
  }
  .more {
    display: flex;
    flex-wrap: wrap;
    gap: var(--sp-3);
    padding-bottom: var(--sp-3);
    border-bottom: 1px solid var(--line);
  }
  .more .slot {
    flex: 0 1 auto;
    min-width: 72px;
  }

  .slot {
    flex: 1 1 0;
    min-width: 0;
    min-height: 46px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: var(--sp-2);
    background: none;
    border: 1px solid var(--line);
    border-radius: var(--r-2);
    color: var(--ink);
    cursor: pointer;
    /* A dynamic glyph can be long — the time slot reads "06:40" — and a slot
       that grew to fit one would move the others under the thumb reaching for
       them. It wraps inside its share instead. */
    overflow-wrap: anywhere;
  }
  .ic {
    font: 15px/1 var(--mono);
    font-variant-numeric: tabular-nums;
  }
  .lb {
    font: 7px/1 var(--px);
    letter-spacing: 0.06em;
    color: var(--sub);
    text-transform: uppercase;
  }
  .slot.on {
    border-color: var(--cy);
    color: var(--cy);
    background: var(--cy-weak);
  }
  .slot.on .lb {
    color: var(--cy);
  }
  /* The one state it is dangerous to forget — see the note on `tone` in
     panels.svelte.ts. */
  .slot.warn {
    border-color: var(--am);
    color: var(--am);
    background: var(--am-weak);
  }
  .slot.warn .lb {
    color: var(--am);
  }
  .slot.off {
    opacity: 0.35;
    cursor: default;
  }

  .fineprint {
    font: var(--fs-micro) / var(--lh-read) var(--sans);
    color: var(--sub);
  }
</style>
